import { sanitizeValue } from '@miq/catalog';
import { Floor, Space } from '@miq/domain/pv';
import { DASH, MiqMoment, UserSettings } from '@miq/utils';
import { FloorWithBuildingInfo, PVColumn } from './column.model';
import { EntityType } from '@miq/domain';
import { StackPlanViewModel } from '../../state/sp-view-state.model';

export const AVAILABILITY_LABEL = '--- Available ---';
export const NON_RENTABLE_LABEL = 'Non-Rentable';
const TENANT_MAX_CHARCHTER = 40;
export const UNKNOWN_TENANT = 'Unspecified Tenant';
export const DIRECT = 'Direct';
export const SUBLEASE = 'Sublease';

/**
 * Get the value that should be displayed according to the Tenants of a space
 * @param floor
 * @returns string
 * @example
 * const floor = {
 *  spaces: [
 *    { lease: { tenant_info: [{ party_organization_display_name: 'Tenant 1' }] } }
 *  ]
 * };
 * const tenantValue = getTenantValueToDisplay(floor);
 * returns 'Tenant 1';
 *
 * const floor = {
 *  spaces: [
 *    { lease: { tenant_info: [{ party_organization_display_name: 'Tenant 1' }] } },
 *    { lease: { tenant_info: [{ party_organization_display_name: 'Tenant 2' }] } },
 *  ]
 * };
 * const tenantValue = getTenantValueToDisplay(floor);
 * returns '2 Tenants';
 */
export const getTenantValueToDisplay = (floor: Floor, context: StackPlanViewModel): string => {
  if (floor.floor_area_sum == null || floor.floor_area_sum === 0) {
    return DASH;
  }

  const allTenant = getTenantsForPVTable(floor, context.propertyType);
  const tenants = allTenant.filter(i => i != null && i != '');
  if (tenants.length === 0) {
    return '-';
  }
  return formatTenantNames(tenants);
};

export function formatTenantNames(tenants: string[]): string {
  let tenantNames = '';
  const tenantNamesArr: string[] = [];
  const totalNumberOfTenants = tenants?.length || 0;

  // Get only the initial tenant keeping space to add `+ mode` if needed
  tenants.forEach(tenant => {
    if (tenant) {
      const isTenantNamesEmpty = !tenantNames?.length;
      const updatedTenantNames = tenantNames.length ? `${tenantNames} | ${tenant}` : tenant;

      // if the updated tenant names length is less than the max character limit plus a buffer, add the tenant
      if (updatedTenantNames?.length < TENANT_MAX_CHARCHTER + 10 || isTenantNamesEmpty) {
        tenantNames = updatedTenantNames;
        tenantNamesArr.push(tenant);
      }
    }
  });

  // Get the difference of tenants not shown
  const missgingTenants = totalNumberOfTenants - tenantNamesArr?.length;
  const moreTenantsText = missgingTenants > 0 ? ` | +${missgingTenants} more` : '';
  tenantNames = tenantNamesArr.join(' | ') + moreTenantsText;

  // If after adding all possible tenants the length is still over the limit, truncate it
  if (tenantNames?.length > TENANT_MAX_CHARCHTER) {
    if (tenantNamesArr.join(' | ').length > TENANT_MAX_CHARCHTER) {
      return tenantNamesArr.join(' | ').substring(0, TENANT_MAX_CHARCHTER + 3) + '...' + moreTenantsText;
    }
  }
  return tenantNames;
}

const getTenants = (floor: Floor): string[] => {
  const entityKey = 'lease';
  const tenantList = floor?.spaces?.filter((e: Space) => e[entityKey] != null) ?? [];

  if (tenantList.length === 0) {
    return [];
  }
  const tenants: string[][] = (tenantList ?? []).map((spaceT: Space) =>
    (spaceT.lease.tenant_info ?? []).map((ti: Record<string, any>) => ti.party_organization_display_name || '')
  );

  return Array.from(new Set(tenants.flat()));
};

export const getTenantsForPVTable = (floor: Floor, selectedUsage: string = null): string[] => {
  const entityKey = 'lease';
  let leaseList = floor?.spaces?.filter((e: Space) => e[entityKey] != null) ?? [];

  if (
    floor.floor_non_rentable_area > 0 &&
    floor.floor_tgt_available_area === 0 &&
    floor.floor_tgt_occupied_area === 0 &&
    floor.floor_non_tgt_area === 0
  ) {
    const spacesWithSize = floor?.spaces.filter(space => space?.non_rentable_space?.non_rentable_size);
    const spacesWithoutSize = floor?.spaces.filter(space => space?.non_rentable_space?.non_rentable_size == null);
    spacesWithSize.sort((a, b) => b?.non_rentable_space?.non_rentable_size - a?.non_rentable_space?.non_rentable_size);
    const sortedSpaces = [...spacesWithSize, ...spacesWithoutSize];
    const nonRentableSpaceTypes = sortedSpaces.reduce((classificationTypes: string[], space) => {
      if (space?.non_rentable_space?.space_classification_type_desc) {
        classificationTypes.push(space?.non_rentable_space.space_classification_type_desc);
      }
      return classificationTypes;
    }, []);
    return [...new Set(nonRentableSpaceTypes)];
  }

  if (floor.floor_non_tgt_area > 0 && floor.floor_tgt_available_area === 0 && floor.floor_tgt_leased_area === 0) {
    const leaseUsages = floor?.spaces.map(space => space?.lease && space.lease?.ref_property_usage_type_desc);
    const availabilityUsages = floor?.spaces.map(
      space => space?.availability && space.availability?.ref_property_usage_type_desc
    );
    const floorUsages = leaseUsages.concat(availabilityUsages);

    const distinctFloorUsages = floorUsages.filter(function (elem, index, self) {
      return index === self.indexOf(elem);
    });
    return distinctFloorUsages;
  }

  if (floor.floor_tgt_available_area > 0 && floor.floor_tgt_leased_area === 0 && floor.floor_non_tgt_area === 0) {
    return [AVAILABILITY_LABEL];
  }

  if (floor?.floor_tgt_leased_area > 0) {
    // Firts filter tenant to get only the ones that match the selected usage
    if (selectedUsage != null) {
      leaseList = leaseList.filter(l => l.lease.ref_property_usage_type_desc === selectedUsage);
    }
    leaseList = leaseList.sort((a, b) => {
      const attribute = a.lease.arrangement_space_area != null ? 'arrangement_space_area' : 'total_area_of_space';
      return b.lease[attribute] - a.lease[attribute];
    });
    return leaseList.flatMap((spaceT: Space) =>
      (spaceT.lease.tenant_info ?? []).map((ti: Record<string, any>) => ti.party_organization_display_name || '')
    );
  }
  return [''];
};

export const getFloorSlabHeight = (floor: FloorWithBuildingInfo) => {
  if (!floor?.floor_slab_height || floor?.floor_slab_height === 0) {
    return '-';
  }
  const integerFeet = Math.floor(floor?.floor_slab_height);
  const fractionalFeet = floor?.floor_slab_height - integerFeet;
  const inches = Math.round(fractionalFeet * 12);
  return `${integerFeet}' ${inches}"`;
};
/**
 * Get the Tenant names as a list of strings
 * takes the party_organization_display_name from the Space Tenant object
 * @param floor
 * @returns string[]
 * @example
 * const floor = {
 *  spaces: [
 *    { lease: { tenant_info: [{ party_organization_display_name: 'Tenant 1' }] } }
 *  ]
 * };
 * const tenants = getTenants(floor);
 * returns ['Tenant 1']
 *
 * const floor = {
 *  spaces: [
 *    { lease: { tenant_info: [{ party_organization_display_name: 'Tenant 1' }] } },
 *    { lease: { tenant_info: [{ party_organization_display_name: 'Tenant 2' }] } },
 *  ]
 * };
 * const tenants = getTenants(floor);
 * returns ['Tenant 1', 'Tenant 2']
 */

/**
 * Get latest expiry date of a Tenant (Lease)
 * @param floor
 * @returns string
 * @example
 * const floor = {
 *  spaces: [
 *    { lease: { space_tenancy_end_date: '2022-01-01' } }
 *  ]
 * };
 * const tenantLXD = getTenantEndDate(floor);
 * returns '1/1/2022'
 */
export const getTenantEndDate = (floor: Floor, context: StackPlanViewModel) => {
  if (floor.floor_area_sum == null || floor.floor_area_sum === 0) {
    return DASH;
  }

  const {
    floor_non_rentable_area,
    floor_tgt_available_area,
    floor_tgt_occupied_area,
    floor_non_tgt_area,
    floor_tgt_leased_area,
  } = floor;

  if (
    (floor_non_rentable_area > 0 &&
      floor_tgt_available_area === 0 &&
      floor_tgt_occupied_area === 0 &&
      floor_non_tgt_area === 0) ||
    (floor_non_tgt_area > 0 && floor_tgt_available_area === 0 && floor_tgt_leased_area === 0) ||
    (floor_tgt_available_area > 0 && floor_tgt_leased_area == 0 && floor_non_tgt_area == 0)
  ) {
    return '-';
  }

  if (floor_tgt_leased_area > 0) {
    return largestTenantEndDate(floor, context.propertyType);
  }
  return ['-'];
};

export const largestTenantEndDate = (floor: Floor, selectedUsage: string = null): any => {
  const entityKey = 'lease';
  let tenantList = floor?.spaces?.filter((space: Space) => space[entityKey] != null) ?? [];
  // Firts filter tenant to get only the ones that match the selected usage
  if (selectedUsage != null) {
    tenantList = tenantList.filter(l => l.lease.ref_property_usage_type_desc === selectedUsage);
  }
  if (tenantList.length) {
    const largestSpaceTenant = tenantList.reduce((prev: Space | null, curr: Space) => {
      const prevArea = prev?.lease?.arrangement_space_area ?? prev?.lease?.total_leased_space ?? 0;
      const currArea = curr.lease?.arrangement_space_area ?? curr.lease?.total_leased_space ?? 0;
      return currArea > prevArea ? curr : prev;
    });

    if (largestSpaceTenant && largestSpaceTenant?.lease) {
      const leaseType = largestSpaceTenant?.lease?.lease_record_type_desc;
      const date = largestSpaceTenant?.lease?.space_tenancy_end_date || largestSpaceTenant?.lease?.lease_end_date;
      if (leaseType === 'Owner Occupied') {
        return date ? `Owner (${MiqMoment.format(date, 'MM/YYYY')})` : 'Owner';
      }
      return date ? MiqMoment.format(date, 'MM/YYYY') : '-';
    }
  }

  return '-';
};

/**
 * Get the floor size of a Flor on sqft
 * @param floor
 * @param column
 * @returns
 * @example
 * const floor = {
 *  floor_size: 24537829,
 *  floor_size_uom_desc: 'sqft'
 * };
 * const column = { name: 'floor_size', options: { format: '.0-2' }};
 * const floorSize = getFloorSize(floor, column);
 * returns '24,537,829'
 */
export const formatSquareFeet = (number: number): string => {
  const formattedAmount = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${formattedAmount} SF`;
};

export const getFloorSize = (floor: FloorWithBuildingInfo, column: PVColumn) => {
  const formatAndReturn = (value: number | string): string => {
    return typeof value === 'number' ? sanitizeValue(formatSquareFeet(value), column) : !value ? '-' : value;
  };

  if (floor.floor_area_sum == null || floor.floor_area_sum === 0) {
    return formatAndReturn(floor.floor_rentable_area || floor.total_gross_area || DASH);
  }

  const {
    floor_non_rentable_area = 0,
    floor_tgt_available_area = 0,
    floor_tgt_occupied_area = 0,
    floor_non_tgt_area = 0,
    floor_tgt_leased_area = 0,
    floor_rentable_area,
    total_gross_area,
  } = floor || {};

  if (
    (floor_non_rentable_area > 0 &&
      floor_tgt_available_area === 0 &&
      floor_tgt_occupied_area === 0 &&
      floor_non_tgt_area === 0) ||
    (floor_non_tgt_area > 0 && floor_tgt_available_area === 0 && floor_tgt_leased_area === 0)
  ) {
    return '0 SF';
  }

  if (floor_tgt_available_area > 0 && floor_tgt_leased_area === 0 && floor_non_tgt_area === 0) {
    return formatAndReturn(
      getFirstNonNullOrEmptyValue(
        floor_rentable_area,
        total_gross_area,
        floor_tgt_available_area + floor_tgt_occupied_area
      )
    );
  }

  if (floor_tgt_leased_area > 0) {
    return formatAndReturn(
      getFirstNonNullOrEmptyValue(
        floor_rentable_area,
        total_gross_area,
        floor_tgt_available_area + floor_tgt_occupied_area + floor_non_rentable_area
      )
    );
  }
  return formatAndReturn(null);
};

/**
 * Get the Tenant value depending of the space type
 * @param space
 * @returns string
 */
const getTenant = (space: Space): { formattedValue: string; rawValue: string[] } => {
  if (space.lease != null) {
    const emptyLabel = UNKNOWN_TENANT;
    const tenants = (getTenants({ spaces: [space] } as Floor) || [])
      .map(e => (e || emptyLabel).trim())
      .sort((a, b) => a.localeCompare(b));
    const formattedTenants = tenants.join('\n ') || emptyLabel;
    return { formattedValue: formattedTenants, rawValue: tenants.length > 0 ? tenants : [emptyLabel] };
  }

  if (space?.non_rentable_space != null) {
    return {
      formattedValue: space.non_rentable_space.space_classification_type_desc ?? DASH,
      rawValue:
        space.non_rentable_space.space_classification_type_desc != null
          ? [space.non_rentable_space.space_classification_type_desc]
          : [],
    };
  }

  return {
    formattedValue: space?.availability != null ? AVAILABILITY_LABEL : DASH,
    rawValue: space?.availability != null ? [AVAILABILITY_LABEL.replaceAll(/-| /g, '')] : [],
  };
};

/**
 *  Get the Property Type value depending of the space type
 * @param space Space
 * @returns string
 */
const getPropertyType = (space: Space) => {
  if (space?.non_rentable_space != null) {
    return NON_RENTABLE_LABEL;
  }

  return space.availability?.ref_property_usage_type_desc ?? space.lease?.ref_property_usage_type_desc ?? DASH;
};

/**
 * Get the SF value depending of the space type
 * @param space
 * @returns
 */
const getSF = (space: Space): { formattedValue: string; rawValue: number } => {
  let size: number;
  const columnConfig = { dataType: 'numeric', options: { format: '0.0-0' } };
  if (space?.non_rentable_space != null) {
    size = space?.non_rentable_space.non_rentable_size;
  }

  if (space?.availability != null) {
    size = space?.availability?.available_space;
  }

  if (space?.lease != null) {
    size = space?.lease?.arrangement_space_area || space?.lease?.total_leased_space;
  }

  return { formattedValue: sanitizeValue(size, columnConfig) ?? DASH, rawValue: size };
};

const getLeaseEndDate = (lease: Record<string, any>) => {
  const endDate = lease?.space_tenancy_end_date || lease?.lease_end_date;
  const leaseType = lease?.lease_record_type_desc;
  const formatted = endDate
    ? MiqMoment.format(endDate, UserSettings.getGlobalDateDisplayFormatPortion('d').toUpperCase())
    : null;

  if (leaseType === 'Owner Occupied') {
    return endDate ? { formattedValue: formatted, rawValue: endDate } : { formattedValue: 'N/A', rawValue: null };
  }

  return {
    formattedValue: formatted ?? DASH,
    rawValue: endDate ?? null,
  };
};

/**
 * Get the End Date value depending of the space type
 * @param space
 * @returns string
 */
const getEndDate = (space: Space): { formattedValue: string; rawValue: string } => {
  return getLeaseEndDate(space?.lease);
};

/*    Lease Type
    - When the record is a Lease
      Then When Sublease (lease:sublease_f)=”Y”,  Then display “Sublease” Else display “Direct”
          And When Space Status (lease:tenancy_status_desc) does not equal “Occupying” 
          Then append “ (tenancy_status_desc)” (e.g. “Direct (Future)”)
 */
const getLeaseTypeLabel = (
  lease: Record<string, any> & {
    sublease_f: boolean;
    space_tenancy_status_desc: string;
    lease_record_type_desc?: string;
  }
) => {
  const status = lease.space_tenancy_status_desc?.trim();
  if (lease.lease_record_type_desc === 'Owner Occupied') {
    return status && status.toLowerCase() !== 'occupying' ? `Owner (${status})` : 'Owner';
  }

  const tenantType = lease.sublease_f ? SUBLEASE : DIRECT;
  return status && status.toLowerCase() !== 'occupying' ? `${tenantType} (${status})` : tenantType;
};

/**
 * Get the lease type value depending of the space type
 * @param space Space
 * @returns string
 */
const getLeaseType = (space: Space) => {
  if (space?.non_rentable_space != null) {
    return NON_RENTABLE_LABEL;
  }

  if (space?.availability != null) {
    return space.availability.ref_lease_type_desc ?? DASH;
  }

  if (space?.lease != null) {
    return getLeaseTypeLabel({
      sublease_f: space.lease.sublease_f,
      space_tenancy_status_desc: space.lease.space_tenancy_status_desc,
      lease_record_type_desc: space.lease.lease_record_type_desc,
    });
  }

  return DASH;
};

/**
 * Validate if a space has the descritpion of a empty space
 * empty space in this context means that the spaces hosul not be shown
 * @param space Space
 * @returns True/False
 */
export const isEmptySpace = (space: Space) =>
  !space.availability?.active_f && !space.lease?.active_f && space.non_rentable_space == null;

/**
 * Get the Entity type of a Space
 * @param space
 * @returns
 */
export const getEntity = (space: Space): EntityType | 'NonRentable' => {
  if (space.non_rentable_space != null) {
    return 'NonRentable';
  }
  return space.availability != null ? EntityType.Availability : EntityType.LeaseComp;
};

/**
 * Get the Entity Id of a Space
 * @param space
 * @returns
 */
export const getSpaceId = (space: Space): number => {
  if (space.non_rentable_space != null) {
    return null;
  }
  return space?.availability?.id || space?.lease?.id;
};

export function getFirstNonNullOrEmptyValue<T>(...values: (T | null | undefined)[]): T | null {
  for (const value of values) {
    if (value) {
      return value;
    }
  }
  return values[values.length - 1] ? values[values.length - 1] : null;
}

/**
 * Get the url of a space record used to redirect the user to the details page
 * @param space
 * @returns
 */
export const getSpaceUrl = (space: Space): string => {
  if (space.non_rentable_space != null) {
    return null;
  }
  return `records/${getEntity(space)}/${space?.availability?.id || space?.lease?.id}/edit`;
};

/**
 * Check if the space has a Lease and active
 * @param space
 * @returns true/false
 */
export const hasValidLease = (space: Record<string, any>) => space?.lease != null && !!space?.lease.active_f;

/**
 * From a Space object get the tenant ids of the Lease if the Lease is valid
 * @param space
 * @returns
 */
const getTenantIds = (space: Space): number[] => {
  if (hasValidLease(space)) {
    return space.lease.tenant_info.map(t => t.company_id).filter(Boolean);
  }
  return [];
};

export const formatFlatFloorSpaceDeatils = {
  getTenant,
  getPropertyType,
  getSF,
  getEndDate,
  getLeaseType,
  getLeaseTypeLabel,
  getLeaseEndDate,
  getTenantIds,
};

export const formatFullFloorDetails = {
  getTenantValueToDisplay,
  getTenants,
  getTenantsForPVTable,
  getTenantEndDate,
  getFloorSize,
  getFloorSlabHeight,
};
